import { useEffect, useCallback, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { layoutModeAtom } from '../atoms/v3/uiAtoms';
import { isVoiceRecordingAtom } from '../atoms/v3/chatInputAtoms';
import { globalKeymapModalOpenAtom } from '../atoms/v3/uiAtoms';
import { isTouchDevice } from '../utils/platformUtils';

// 快捷键上下文接口
export interface ShortcutContext {
  layoutMode: 'desktop' | 'mobile';
  currentPage: string;
  isInputFocused: boolean;
  activeElement: Element | null;
  isModalOpen: boolean;
  isVoiceRecording: boolean;
  event: KeyboardEvent;
}

// 快捷键命令接口
export interface ShortcutCommand {
  id: string;
  keys: string[];
  condition?: (context: ShortcutContext) => boolean;
  action: (context: ShortcutContext) => void;
  priority: number;
  description: string;
  category: string;
  enableOnFormTags?: boolean; // 是否在表单元素中生效
}

// 快捷键管理器类
export class ShortcutManager {
  private commands: Map<string, ShortcutCommand> = new Map();
  private listeners: Set<(commands: ShortcutCommand[]) => void> = new Set();

  // 注册快捷键命令
  register(command: ShortcutCommand) {
    if (this.commands.has(command.id)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`ShortcutManager: Duplicate command registration detected: ${command.id}`);
      }
    }
    this.commands.set(command.id, command);
    this.notifyListeners();
  }

  // 注销快捷键命令
  unregister(commandId: string) {
    this.commands.delete(commandId);
    this.notifyListeners();
  }

  // 批量注册（带运行时去重）
  registerBatch(commands: ShortcutCommand[]) {
    const deduplicatedCommands: ShortcutCommand[] = [];
    const duplicateInfo: Array<{existing: ShortcutCommand, new: ShortcutCommand, action: string}> = [];

    commands.forEach(command => {
      const existingCommand = this.commands.get(command.id);

      if (existingCommand) {
        // 发现重复命令，比较优先级
        if (command.priority > existingCommand.priority) {
          // 新命令优先级更高，替换现有命令
          deduplicatedCommands.push(command);
          duplicateInfo.push({
            existing: existingCommand,
            new: command,
            action: 'replaced (higher priority)'
          });
        } else {
          // 现有命令优先级更高或相等，保持现有命令
          duplicateInfo.push({
            existing: existingCommand,
            new: command,
            action: 'kept existing (higher or equal priority)'
          });
        }
      } else {
        // 新命令，直接添加
        deduplicatedCommands.push(command);
      }
    });

    // 注册去重后的命令
    deduplicatedCommands.forEach(command => this.commands.set(command.id, command));

    // 开发环境下输出去重信息
    if (process.env.NODE_ENV === 'development' && duplicateInfo.length > 0) {
      console.group(`ShortcutManager: Processed ${duplicateInfo.length} duplicate commands`);
      duplicateInfo.forEach(info => {
        console.log(`${info.new.id}: ${info.action}`, {
          existing: `${info.existing.description} (priority: ${info.existing.priority})`,
          new: `${info.new.description} (priority: ${info.new.priority})`
        });
      });
      console.groupEnd();
    }

    this.notifyListeners();
  }

  // 批量注销
  unregisterBatch(commandIds: string[]) {
    commandIds.forEach(id => this.commands.delete(id));
    this.notifyListeners();
  }

  // 获取所有命令
  getAllCommands(): ShortcutCommand[] {
    return Array.from(this.commands.values()).sort((a, b) => b.priority - a.priority);
  }

  // 获取当前生效的命令
  getActiveCommands(context: ShortcutContext): ShortcutCommand[] {
    return this.getAllCommands().filter(command => {
      // 检查是否在表单元素中且命令不允许在表单中执行
      if (context.isInputFocused && !command.enableOnFormTags) {
        return false;
      }
      
      // 检查条件
      return !command.condition || command.condition(context);
    });
  }

  // 处理键盘事件
  handleKeyDown(e: KeyboardEvent, context: ShortcutContext): boolean {
    const keyCombo = this.getKeyCombo(e);
    const activeCommands = this.getActiveCommands(context);

    // 按优先级查找匹配的命令
    for (const command of activeCommands) {
      if (this.matchesKeys(keyCombo, command.keys)) {
        // 阻止默认行为和事件传播
        e.preventDefault();
        e.stopPropagation();

        try {
          command.action(context);
        } catch (error) {
          console.error(`Error executing shortcut command ${command.id}:`, error);
        }

        return true; // 表示事件已处理
      }
    }

    return false; // 表示事件未处理
  }

  // 添加监听器
  addListener(listener: (commands: ShortcutCommand[]) => void) {
    this.listeners.add(listener);
  }

  // 移除监听器
  removeListener(listener: (commands: ShortcutCommand[]) => void) {
    this.listeners.delete(listener);
  }

  // 通知监听器
  private notifyListeners() {
    const commands = this.getAllCommands();
    this.listeners.forEach(listener => listener(commands));
  }

  // 获取按键组合字符串
  private getKeyCombo(e: KeyboardEvent): string {
    const parts: string[] = [];
    
    if (e.ctrlKey) parts.push('ctrl');
    if (e.metaKey) parts.push('cmd');
    if (e.altKey) parts.push('alt');
    if (e.shiftKey) parts.push('shift');
    
    // 处理特殊键
    let key = e.key.toLowerCase();
    if (key === ' ') key = 'space';
    if (key === 'escape') key = 'esc';
    if (key === 'arrowup') key = 'up';
    if (key === 'arrowdown') key = 'down';
    if (key === 'arrowleft') key = 'left';
    if (key === 'arrowright') key = 'right';
    
    parts.push(key);
    
    return parts.join('+');
  }

  // 检查按键是否匹配
  private matchesKeys(keyCombo: string, commandKeys: string[]): boolean {
    return commandKeys.some(commandKey => {
      const normalizedCommandKey = commandKey.toLowerCase().replace(/\s+/g, '');
      const normalizedKeyCombo = keyCombo.replace(/\s+/g, '');
      return normalizedCommandKey === normalizedKeyCombo;
    });
  }

  // 清理所有命令
  clear() {
    this.commands.clear();
    this.notifyListeners();
  }
}

// 全局快捷键管理器实例（确保单例）
let globalShortcutManager: ShortcutManager | null = null;

const getGlobalShortcutManager = (): ShortcutManager => {
  if (!globalShortcutManager) {
    globalShortcutManager = new ShortcutManager();
    console.log('ShortcutManager: Created new global instance');
  }
  return globalShortcutManager;
};

// 事件监听器引用计数和代理机制
let eventListenerRefCount = 0;
let globalKeyDownHandler: ((e: KeyboardEvent) => void) | null = null;
let globalKeyUpHandler: ((e: KeyboardEvent) => void) | null = null;

// 代理函数，确保始终调用最新的处理器
const keyDownProxy = (e: KeyboardEvent) => {
  if (globalKeyDownHandler) {
    globalKeyDownHandler(e);
  }
};

const keyUpProxy = (e: KeyboardEvent) => {
  if (globalKeyUpHandler) {
    globalKeyUpHandler(e);
  }
};

// React Hook
export const useShortcutManager = () => {
  const layoutMode = useAtomValue(layoutModeAtom);
  const isVoiceRecording = useAtomValue(isVoiceRecordingAtom);
  const [isModalOpen, setIsModalOpen] = useAtom(globalKeymapModalOpenAtom);
  const managerRef = useRef(getGlobalShortcutManager());

  // 长按 CMD 键的状态管理
  const cmdPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCmdPressedRef = useRef(false);

  // 获取当前页面类型
  const getCurrentPageType = useCallback((): string => {
    const path = window.location.pathname;
    if (path.includes('/v3/taskv2')) return 'taskv2';
    if (path.includes('/v3/chat')) return 'chat';
    if (path.includes('/v3/me')) return 'me';
    return 'unknown';
  }, []);

  // 创建上下文
  const createContext = useCallback((e: KeyboardEvent): ShortcutContext => {
    const activeElement = document.activeElement;
    const isInputFocused = 
      activeElement?.tagName === 'INPUT' ||
      activeElement?.tagName === 'TEXTAREA' ||
      (activeElement as HTMLElement)?.contentEditable === 'true';

    return {
      layoutMode,
      currentPage: getCurrentPageType(),
      isInputFocused,
      activeElement,
      isModalOpen,
      isVoiceRecording,
      event: e
    };
  }, [layoutMode, isModalOpen, isVoiceRecording, getCurrentPageType]);

  // 键盘事件处理器
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // 禁用长按 CMD 键显示快捷键帮助功能
    // if (e.key === 'Meta' && !isTouchDevice() && !isCmdPressedRef.current) {
    //   isCmdPressedRef.current = true;
    //   cmdPressTimerRef.current = setTimeout(() => {
    //     setIsModalOpen(true);
    //   }, 2000);
    //   return;
    // }

    const context = createContext(e);
    managerRef.current.handleKeyDown(e, context);
  }, [createContext, setIsModalOpen]);

  // 键盘释放事件处理器
  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    // 禁用 CMD 键释放时清除定时器的逻辑
    // if (e.key === 'Meta') {
    //   isCmdPressedRef.current = false;
    //   if (cmdPressTimerRef.current) {
    //     clearTimeout(cmdPressTimerRef.current);
    //     cmdPressTimerRef.current = null;
    //   }
    // }
  }, []);

  // 设置全局事件监听器（使用引用计数和代理机制）
  useEffect(() => {
    eventListenerRefCount++;
    console.log(`ShortcutManager: Event listener ref count: ${eventListenerRefCount}`);

    // 只在第一次调用时添加代理事件监听器
    if (eventListenerRefCount === 1) {
      console.log('ShortcutManager: Adding global proxy event listeners');
      window.addEventListener('keydown', keyDownProxy, false);
      window.addEventListener('keyup', keyUpProxy, false);
    }

    // 始终更新处理器引用，代理函数会调用最新的处理器
    globalKeyDownHandler = handleKeyDown;
    globalKeyUpHandler = handleKeyUp;

    return () => {
      eventListenerRefCount--;
      console.log(`ShortcutManager: Event listener ref count: ${eventListenerRefCount}`);

      // 只在最后一次卸载时移除事件监听器
      if (eventListenerRefCount === 0) {
        console.log('ShortcutManager: Removing global proxy event listeners');
        window.removeEventListener('keydown', keyDownProxy, false);
        window.removeEventListener('keyup', keyUpProxy, false);
        globalKeyDownHandler = null;
        globalKeyUpHandler = null;
      }

      // 清理定时器
      if (cmdPressTimerRef.current) {
        clearTimeout(cmdPressTimerRef.current);
        cmdPressTimerRef.current = null;
      }
    };
  }, [handleKeyDown, handleKeyUp]);

  return {
    manager: managerRef.current,
    register: managerRef.current.register.bind(managerRef.current),
    unregister: managerRef.current.unregister.bind(managerRef.current),
    registerBatch: managerRef.current.registerBatch.bind(managerRef.current),
    unregisterBatch: managerRef.current.unregisterBatch.bind(managerRef.current),
    getAllCommands: managerRef.current.getAllCommands.bind(managerRef.current),
    getActiveCommands: (context?: Partial<ShortcutContext>) => {
      const fullContext = context ? { ...createContext(new KeyboardEvent('keydown')), ...context } : createContext(new KeyboardEvent('keydown'));
      return managerRef.current.getActiveCommands(fullContext);
    }
  };
};

export default getGlobalShortcutManager;
